import { Editor, track } from '@tldraw/tldraw'
import { ColorSwatch } from 'components/Design/ColorSwatch/ColorSwatch'
import { ImageShape } from 'components/VisualStudioPlayground/Shapes/ImageShape/Types'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { SolidColor } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { getColors } from 'components/VisualStudioPlayground/VisualEditor/helpers'
import { ImageShapeUtil } from 'components/VisualStudioPlayground/Shapes/ImageShape/ImageShapeUtil'
import { BaseColorSwatchProps } from 'components/Design/ColorSwatch/Types'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

interface Props {
  editor: Editor
  shape: ImageShape
}

interface SwatchProps extends BaseColorSwatchProps {
  original: SolidColor
  editor: Editor
  shape: ImageShape
}

const Swatch = observer(({ original, color, editor, shape }: SwatchProps) => {
  const { designStore } = useRootStore()
  const shapeUtil = editor.getShapeUtil<ImageShape>(shape) as ImageShapeUtil
  const { isMobile } = useResponsiveDesign()

  const handlePress = () => {
    if (!designStore) return

    designStore.openPanel({
      type: 'colors',
      styleProp: null,
      onSelectionChange: (editor: Editor) => {
        const selectedShape = editor.getOnlySelectedShape()
        if (selectedShape?.id !== shape.id) {
          designStore.closePanel()
        }
      },
      defaultColor: color,
      colors: Array.from(getColors(editor).values()),
      onChange: (color) => {
        if (color.type === 'solidColor') {
          shapeUtil.colorise(shape, {
            ...shape.meta.colorSet,
            [original.color]: color.color,
          })
        }
      },
      allowTransparency: false,
      allowGradient: false,
    })
  }

  return <ColorSwatch color={color} onPress={handlePress} size={isMobile ? 42 : 32} />
})

export const ColorSetSection = track(({ editor, shape }: Props) => {
  const colorSet = shape.meta.colorSet

  if (!colorSet) return null

  return (
    <>
      {Object.entries(colorSet).map(([original, rendered], i) => (
        <Swatch
          key={`${rendered}-${i}`}
          shape={shape}
          original={{ type: 'solidColor', color: original, opacity: 1 }}
          color={{ type: 'solidColor', color: rendered, opacity: 1 }}
          editor={editor}
        />
      ))}
    </>
  )
})
